import { Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Home, Send } from "lucide-react";
import { useState } from "react";
import { useSettings } from "@/lib/use-content";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function SiteFooter() {
  const s = useSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("יש למלא את כל השדות");
      return;
    }
    if (!consent) {
      toast.error("יש לאשר את מדיניות הפרטיות כדי לשלוח");
      return;
    }
    setSending(true);
    const ok = await api.contact.send(form);
    setSending(false);
    if (!ok) {
      toast.error("שליחה נכשלה, נסו שוב");
      return;
    }
    toast.success("ההודעה נשלחה, נחזור אליכם בהקדם");
    setForm({ name: "", email: "", message: "" });
    setConsent(false);
  };

  return (
    <footer className="mt-20 border-t border-border/40 bg-primary text-primary-foreground">
      <div className="container mx-auto grid gap-8 px-4 py-12 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-md gradient-gold text-gold-foreground font-display font-bold shadow-card">
              ת״ח
            </div>
            <h3 className="font-display text-xl font-bold">ישיבת תורת חיים</h3>
          </div>
          <p className="mt-4 text-sm text-primary-foreground/80 leading-relaxed">
            התורה היא החיים של כולנו. מסגרת תורנית חמה ומקצועית לבחורים יראי שמיים השואפים לגדולות.
          </p>
        </div>

        <div>
          <h4 className="font-display text-lg font-bold mb-4">ניווט</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:text-gold transition-colors">בית</Link></li>
            <li><Link to="/about" className="hover:text-gold transition-colors">אודות</Link></li>
            <li><Link to="/programs" className="hover:text-gold transition-colors">מענים וסדר יום</Link></li>
            <li><Link to="/register" className="hover:text-gold transition-colors">הרשמה</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-lg font-bold mb-4">צור קשר</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gold" />
              <a href="tel:03-6761114" dir="ltr">03-6761114</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 text-gold mt-0.5" />
              <span>קהילות יעקב 61, בני ברק</span>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gold" />
              <a href="mailto:TORATCHAIMBB@gmail.com">TORATCHAIMBB@gmail.com</a>
            </li>
            <li className="flex items-start gap-2">
              <Home className="h-4 w-4 text-gold mt-0.5" />
              <span>מען למכתבים: קהילות יעקב 56, בני ברק 5164509</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-lg font-bold mb-4">שלחו לנו הודעה</h4>
          <form onSubmit={submit} className="space-y-2">
            <div className="space-y-1">
              <Label htmlFor="footer-name" className="text-xs text-primary-foreground/80">שם</Label>
              <Input
                id="footer-name"
                placeholder="שם"
                aria-label="שם"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="footer-email" className="text-xs text-primary-foreground/80">אימייל</Label>
              <Input
                id="footer-email"
                type="email"
                placeholder="אימייל"
                aria-label="אימייל"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="footer-message" className="text-xs text-primary-foreground/80">הודעה</Label>
              <Textarea
                id="footer-message"
                placeholder="הודעה"
                aria-label="הודעה"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 resize-none"
              />
            </div>
            <div className="flex items-start gap-2 py-1">
              <Checkbox
                id="footer-consent"
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                className="border-primary-foreground/40 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
              />
              <Label htmlFor="footer-consent" className="text-xs text-primary-foreground/80 leading-snug cursor-pointer">
                קראתי ומסכים ל{" "}
                <Link to="/privacy" className="underline hover:text-primary-foreground">מדיניות הפרטיות</Link>
              </Label>
            </div>
            <Button
              type="submit"
              disabled={sending}
              size="sm"
              className="w-full bg-gold text-gold-foreground hover:bg-gold/90"
            >
              <Send className="h-4 w-4 ml-1" />
              {sending ? "שולח..." : "שליחה"}
            </Button>
          </form>
        </div>
      </div>
      <div className="border-t border-primary-foreground/10 py-4 text-center text-xs text-primary-foreground/60 flex flex-wrap justify-center gap-4">
        <span>© {new Date().getFullYear()} ישיבת תורת חיים. כל הזכויות שמורות.</span>
        <Link to="/accessibility" className="underline hover:text-primary-foreground">הצהרת נגישות</Link>
        <Link to="/privacy" className="underline hover:text-primary-foreground">מדיניות פרטיות</Link>
      </div>
    </footer>
  );
}
